import React, { useEffect, useState } from 'react'
import {
  OcrField,
  FIELD_LABELS,
  REQUIRED_FIELD_KEYS,
  EXPECTED_FIELD_KEYS,
  CASE_FIELD_KEYS,
  INSURANCE_FIELD_KEYS,
  INSURANCE_FIELD_LABELS,
  SpecimenItem,
  InsuranceFields,
  WorkflowStatus,
  buildPatchMappedFields,
} from '../../types'
import {
  CONFIDENCE_COLORS,
  CONFIDENCE_INDICATORS,
  NOT_FOUND_LABEL,
  REQUIRED_FIELD_MARKER,
  CONFIDENCE_PRECISION,
  CONFIDENCE_MULTIPLIER,
  AZURE_READ_SCORE_HEADER,
  AZURE_READ_SCORE_TOOLTIP,
  AZURE_READ_SCORE_VALUE_COLOR,
} from './constants'
import { tableStyles } from './styles'

interface Props {
  documentId: string
  workflowStatus: WorkflowStatus
  mappedFields: Record<string, OcrField>
  specimens: SpecimenItem[]
  insurance: InsuranceFields
  evaluatedFields: Array<{
    key: string
    value: string | undefined
    confidence: number
    passed: boolean
  }>
  isSaving: boolean
  onSave: (mappedFields: Record<string, OcrField>) => Promise<void>
  onApprove: (mappedFields: Record<string, OcrField>) => Promise<void>
}

const formatConfidence = (confidence: number): string => {
  if (confidence === 0) return NOT_FOUND_LABEL
  return `${(confidence * CONFIDENCE_MULTIPLIER).toFixed(CONFIDENCE_PRECISION)}%`
}

const getConfidenceColor = (confidence: number, passed: boolean, isRequired: boolean): string => {
  if (confidence === 0) return isRequired ? CONFIDENCE_COLORS.low : CONFIDENCE_COLORS.medium
  if (passed) return CONFIDENCE_COLORS.high
  return CONFIDENCE_COLORS.medium
}

const getConfidenceIndicator = (confidence: number, passed: boolean, isRequired: boolean): string => {
  if (confidence === 0) return isRequired ? CONFIDENCE_INDICATORS.low : CONFIDENCE_INDICATORS.medium
  if (passed) return CONFIDENCE_INDICATORS.high
  return CONFIDENCE_INDICATORS.medium
}

const formatAzureReadScorePercent = (confidence: number): string =>
  `${(confidence * CONFIDENCE_MULTIPLIER).toFixed(CONFIDENCE_PRECISION)}%`

const AzureReadScoreHeader: React.FC = () => (
  <th style={tableStyles.headerCellCenter}>
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
      }}
    >
      {AZURE_READ_SCORE_HEADER}
      <span
        title={AZURE_READ_SCORE_TOOLTIP}
        aria-label={AZURE_READ_SCORE_TOOLTIP}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          border: '1px solid #999',
          color: '#666',
          fontSize: '11px',
          fontWeight: 600,
          cursor: 'help',
          lineHeight: 1,
        }}
      >
        i
      </span>
    </span>
  </th>
)

const renderNotFoundScore = (
  confidence: number,
  passed: boolean,
  isRequired: boolean
): React.ReactNode => (
  <span style={{ color: getConfidenceColor(confidence, passed, isRequired) }}>
    {getConfidenceIndicator(confidence, passed, isRequired)}{' '}
    {formatConfidence(confidence)}
  </span>
)

const renderAzureReadScoreCell = (options: {
  hasValue: boolean
  confidence: number
  passed: boolean
  isRequired: boolean
  wasEdited?: boolean
}): React.ReactNode => {
  const { hasValue, confidence, passed, isRequired, wasEdited } = options
  if (!hasValue) {
    return renderNotFoundScore(confidence, passed, isRequired)
  }
  return (
    <span style={{ color: AZURE_READ_SCORE_VALUE_COLOR }}>
      {wasEdited ? 'Edited' : formatAzureReadScorePercent(confidence)}
    </span>
  )
}

type FieldRow = {
  key: string
  isRequired: boolean
}

const PATIENT_FIELDS: FieldRow[] = [
  ...REQUIRED_FIELD_KEYS.map((key) => ({ key, isRequired: true })),
  ...EXPECTED_FIELD_KEYS.map((key) => ({ key, isRequired: false })),
]

const CASE_FIELDS: FieldRow[] = CASE_FIELD_KEYS.map((key) => ({ key, isRequired: false }))

const INSURANCE_FIELDS: FieldRow[] = INSURANCE_FIELD_KEYS.map((key) => ({
  key,
  isRequired: false,
}))

const specimenRowHasValue = (specimen: SpecimenItem): boolean =>
  [specimen.subsite, specimen.type, specimen.description].some((v) => Boolean(v?.trim()))

const FieldsTable: React.FC<Props> = ({
  documentId,
  workflowStatus,
  mappedFields,
  specimens,
  insurance,
  evaluatedFields,
  isSaving,
  onSave,
  onApprove,
}) => {
  const [editedValues, setEditedValues] = useState<Record<string, string>>({})
  const [editingKey, setEditingKey] = useState<string | null>(null)

  useEffect(() => {
    setEditedValues({})
    setEditingKey(null)
  }, [documentId, mappedFields])

  const readOnly = workflowStatus === 'approved' || workflowStatus === 'published'

  const buildMappedFieldsForPatch = (): Record<string, OcrField> =>
    buildPatchMappedFields(mappedFields, editedValues)

  const hasEdits = Object.entries(editedValues).some(([key, value]) => {
    const original = mappedFields[key]?.value ?? ''
    return value !== original
  })

  const handleSave = async () => {
    await onSave(buildMappedFieldsForPatch())
  }

  const handleApprove = async () => {
    await onApprove(buildMappedFieldsForPatch())
  }

  const handleEdit = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleBlur = () => setEditingKey(null)

  const renderRows = (fields: FieldRow[]) =>
    fields.map(({ key, isRequired }) => {
      const field = mappedFields[key]
      const evaluated = evaluatedFields.find((f) => f.key === key)
      const confidence = field?.confidence ?? 0
      const originalValue = field?.value ?? ''
      const displayValue = editedValues[key] ?? originalValue
      const isEditing = editingKey === key
      const passed = evaluated?.passed ?? confidence > 0
      const wasEdited = editedValues[key] !== undefined && editedValues[key] !== originalValue

      return (
        <tr key={key} style={tableStyles.row}>
          <td style={tableStyles.cell}>
            {FIELD_LABELS[key] ?? key}
            {isRequired && (
              <span style={tableStyles.requiredMarker}>{REQUIRED_FIELD_MARKER}</span>
            )}
          </td>
          <td style={tableStyles.cell}>
            {isEditing ? (
              <input
                autoFocus
                value={displayValue}
                onChange={(e) => handleEdit(key, e.target.value)}
                onBlur={handleBlur}
                disabled={readOnly || isSaving}
                style={{
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #c0392b',
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            ) : (
              <span
                onClick={() => !readOnly && !isSaving && setEditingKey(key)}
                style={{
                  color: displayValue ? '#333' : '#999',
                  cursor: readOnly || isSaving ? 'default' : 'text',
                  display: 'block',
                  minHeight: '20px',
                  whiteSpace: 'pre-wrap',
                  borderBottom: wasEdited ? '1px solid #e67e22' : '1px solid transparent',
                }}
                title="Click to edit"
              >
                {displayValue || '—'}
              </span>
            )}
          </td>
          <td style={tableStyles.confidenceCell}>
            {renderAzureReadScoreCell({
              hasValue: Boolean(displayValue.trim()),
              confidence,
              passed,
              isRequired,
              wasEdited,
            })}
          </td>
        </tr>
      )
    })

  const renderInsuranceRows = () =>
    INSURANCE_FIELDS.map(({ key }) => {
      const field = insurance[key as keyof InsuranceFields]
      const confidence = field?.confidence ?? 0
      const displayValue = field?.value ?? ''

      return (
        <tr key={key} style={tableStyles.row}>
          <td style={tableStyles.cell}>{INSURANCE_FIELD_LABELS[key] ?? key}</td>
          <td style={{ ...tableStyles.cell, whiteSpace: 'pre-wrap' }}>
            <span style={{ color: displayValue ? '#333' : '#999' }}>
              {displayValue || '—'}
            </span>
          </td>
          <td style={tableStyles.confidenceCell}>
            {renderAzureReadScoreCell({
              hasValue: Boolean(displayValue.trim()),
              confidence,
              passed: confidence > 0,
              isRequired: false,
            })}
          </td>
        </tr>
      )
    })

  return (
    <div style={tableStyles.container}>
      <table style={tableStyles.table}>
        <thead>
          <tr style={tableStyles.header}>
            <th style={tableStyles.headerCell}>Field</th>
            <th style={tableStyles.headerCell}>Value</th>
            <AzureReadScoreHeader />
          </tr>
        </thead>
        <tbody>{renderRows(PATIENT_FIELDS)}</tbody>
      </table>

      <h3 style={{ margin: '20px 16px 8px', fontSize: '15px', fontWeight: 600, color: '#444' }}>
        Case Information
      </h3>
      <table style={tableStyles.table}>
        <thead>
          <tr style={tableStyles.header}>
            <th style={tableStyles.headerCell}>Field</th>
            <th style={tableStyles.headerCell}>Value</th>
            <AzureReadScoreHeader />
          </tr>
        </thead>
        <tbody>{renderRows(CASE_FIELDS)}</tbody>
      </table>

      <h3 style={{ margin: '20px 16px 8px', fontSize: '15px', fontWeight: 600, color: '#444' }}>
        Specimens
      </h3>
      <table style={tableStyles.table}>
        <thead>
          <tr style={tableStyles.header}>
            <th style={tableStyles.headerCell}>Jar</th>
            <th style={tableStyles.headerCell}>Location</th>
            <th style={tableStyles.headerCell}>Type</th>
            <th style={tableStyles.headerCell}>Description</th>
            <AzureReadScoreHeader />
          </tr>
        </thead>
        <tbody>
          {specimens.length === 0 ? (
            <tr style={tableStyles.row}>
              <td colSpan={5} style={{ ...tableStyles.cell, color: '#999' }}>
                —
              </td>
            </tr>
          ) : (
            specimens.map((specimen) => (
              <tr key={specimen.order} style={tableStyles.row}>
                <td style={tableStyles.cell}>{specimen.letter}</td>
                <td style={tableStyles.cell}>{specimen.subsite ?? '—'}</td>
                <td style={tableStyles.cell}>{specimen.type ?? '—'}</td>
                <td style={{ ...tableStyles.cell, whiteSpace: 'pre-wrap' }}>
                  {specimen.description ?? '—'}
                </td>
                <td style={tableStyles.confidenceCell}>
                  {renderAzureReadScoreCell({
                    hasValue: specimenRowHasValue(specimen),
                    confidence: specimen.confidence,
                    passed: specimen.confidence > 0,
                    isRequired: false,
                  })}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3 style={{ margin: '20px 16px 8px', fontSize: '15px', fontWeight: 600, color: '#444' }}>
        Insurance Information
      </h3>
      <table style={tableStyles.table}>
        <thead>
          <tr style={tableStyles.header}>
            <th style={tableStyles.headerCell}>Field</th>
            <th style={tableStyles.headerCell}>Value</th>
            <AzureReadScoreHeader />
          </tr>
        </thead>
        <tbody>{renderInsuranceRows()}</tbody>
      </table>

      {!readOnly && (
        <div style={{ display: 'flex', gap: '12px', padding: '16px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !hasEdits}
            style={{
              padding: '8px 16px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: hasEdits ? '#fff' : '#f5f5f5',
              cursor: isSaving || !hasEdits ? 'not-allowed' : 'pointer',
            }}
          >
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={isSaving}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#c0392b',
              color: '#fff',
              cursor: isSaving ? 'not-allowed' : 'pointer',
            }}
          >
            {isSaving ? 'Saving…' : 'Approve'}
          </button>
        </div>
      )}

      {readOnly && (
        <div style={{ padding: '16px', color: '#666', fontSize: '14px', textAlign: 'right' }}>
          {workflowStatus === 'published' ? 'Published - read only' : 'Approved - read only'}
        </div>
      )}
    </div>
  )
}

export default FieldsTable
